import { ExtraScopeEnum } from '@koob/enums';
import useTripStore from '../../store/useTripStore';
import { useLastAddedExperienceId } from '../../hooks/useTripStoreSelectors';
import { cancelAddedExperience } from '../../logic/cancelAddedExperience';
import type { NotifyFn, Translate } from './types';

interface ExtrasModalParams {
  dayIndex: number;
  experience: any;
  tripFilters: any;
  days: any[];
  selectedExtras: any;
  setSelectedExtras: (extras: any[]) => void;
  pricing: {
    priceObject: any;
    extrasPrices?: any[];
    experienceExtras: any[];
    extrasList: any[];
  };
  updateExtrasForDay: (
    dayIndex: number,
    updateFn: (extras: any[]) => any[],
  ) => void;
  transitions: {
    nextExperience: any;
    recommendedExtras: any[];
    extraRecommendationMode: boolean;
  };
  modal: {
    setShowExtrasModal: (open: boolean) => void;
    showExtrasModal: boolean;
    showDayOptions: boolean;
  };
  notify?: NotifyFn;
  t: Translate;
}

// The props the app's additional-services modal is rendered with: which extras
// it lists (the recommendations, or the day's / experience's own), what is
// selected, and how to back out of a program that was just added.
export function useTripDayExperienceExtrasModal({
  dayIndex,
  experience,
  tripFilters,
  days,
  selectedExtras,
  setSelectedExtras,
  pricing,
  updateExtrasForDay,
  transitions,
  modal,
  notify,
  t,
}: ExtrasModalParams) {
  const experiencesMap = useTripStore((state) => state.experiences);
  const updateDays = useTripStore((state) => state.updateDays);
  const lastAddedExperienceId = useLastAddedExperienceId();
  const { showDayOptions } = modal;

  const experienceIndexPosition = days?.findIndex(
    (item) =>
      item?.experienceId === experience?.id &&
      item?.dayIndex === dayIndex - (experience?.dayIndex - 1) + 1,
  );

  const currentExtrasList = transitions.extraRecommendationMode
    ? transitions.recommendedExtras
    : pricing.extrasList.filter(
        (e: any) =>
          !experience?.programs?.some(
            (p: any) =>
              !showDayOptions &&
              p.suggestedExperiences?.some((exp: any) => exp.id === e.id),
          ),
      );

  const onCancelAdd = () => {
    const newDays = cancelAddedExperience({
      days,
      experiencesMap,
      lastAddedExperienceId,
      experienceIndexPosition,
    });

    if (newDays) {
      updateDays(newDays);
      modal.setShowExtrasModal(false);
    }
  };

  return {
    open: modal.showExtrasModal,
    setOpen: modal.setShowExtrasModal,
    experience,
    experienceComposition: tripFilters?.composition,
    extrasScope: showDayOptions ? ExtraScopeEnum.DAY : ExtraScopeEnum.GLOBAL,
    extras: currentExtrasList,
    selectedExtras: showDayOptions ? selectedExtras : pricing.experienceExtras,
    setSelectedExtras,
    updateExtrasForDay,
    prices: pricing.extrasPrices,
    currency: pricing.priceObject?.currency,
    onContinue: (hasChanged: boolean) => {
      if (hasChanged) {
        notify?.(t('experiences:detail.optionsSaved'));
      }
    },
    recommendationMode: transitions.extraRecommendationMode,
    dayIndex,
    programDayIndex: experience?.dayIndex - 1,
    serviceDayIndex:
      showDayOptions && !transitions.extraRecommendationMode
        ? experience?.dayIndex - 1
        : undefined,
    source: 'trip',
    nextExperience: transitions.nextExperience,
    onCancelAdd,
  };
}
